// 거래소 규칙(docs/PARTY-TRADE-PRD.md 2·4장, 레인 D 2단계). 서버(src/server/market.ts)와 클라(거래소 창)가 같이 쓴다.
// - 개체 검사(validTradeItem): 드롭 규칙(gear.ts rolledItem) 범위 밖의 장비는 받지 않는다 — 클라가 만든 개체가 처음 서버에 들어오는 곳의 방어선.
// - 레인 B 어댑터: isTradable·tradeRef 두 함수만 거래 가능 여부·시세 기준값을 정한다(레인 B 필드가 들어오면 여기만 바꾼다).
// - 수수료·최소 입찰·가격 범위·제한 수치(가안 — 운영 지표로 다시 정한다).
// 순수 데이터·함수만 둔다.
import { DIFFICULTY_ILVL_ADD } from './content/economy';
import { isGemCode } from './content/consumables';
import { uniquesFor } from './content/items';
import { GRADES } from './content/items';
import type { Grade } from './content/types';
import { ENHANCE_MAX } from './content/world';
import { affixDef, equipDef, itemSockets, namedOf, tradeInfo, uniqueOf, type GearItem } from './gear';

export const MARKET = {
  /** 판매 수수료(소각, ECONOMY 거래소 줄) */
  feeRate: 0.05,
  minPrice: 1,
  maxPrice: 10_000_000,
  hours: [12, 24, 48] as readonly number[],
  /** 동시 등록 상한(ECONOMY) */
  maxActive: 10,
  /** 하루(24시간 창) 등록·장비 맡기기·골드 맡기기 상한 */
  listsPerDay: 20,
  depositsPerDay: 30,
  goldDepositPerDay: 200_000,
  /** 최소 입찰 증가(비율, 최소 1골드) */
  minIncrement: 0.05,
  /** 마감 5분 안 입찰은 5분 연장(최대 6번) */
  snipeWindowMs: 5 * 60_000,
  snipeExtendMs: 5 * 60_000,
  maxExtends: 6,
  /** 계정 나이: 판매·입찰 72시간(+ Google 연동), 구매 24시간 */
  sellAgeMs: 72 * 3_600_000,
  buyAgeMs: 24 * 3_600_000,
  pageSize: 20,
  /** 금고 잔액 상한(정상 플레이로 닿기 어려운 값) */
  walletMax: 1_000_000_000,
  /** 7일 시세 */
  priceWindowMs: 7 * 86_400_000,
} as const;

const GRADE_IDS: readonly Grade[] = GRADES.map((g) => g.id);
const UID_RE = /^[A-Za-z0-9:_.#-]{3,80}$/;
const nat = (v: unknown, max: number): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= max;

/**
 * 장비 개체가 드롭 규칙 안인지. 통과하면 정리한 사본(알려진 필드만), 아니면 사유 문자열.
 * 정의 존재, 등급, 아이템 레벨(정의 레벨 + 난이도 가산 0·10·20), 옵션(정의 풀 안·중복 없음·값 범위·개수), 강화 0..15, 천장 배열, 소켓, 전설 고유 효과.
 */
export function validTradeItem(raw: unknown): GearItem | string {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 'shape';
  const it = raw as Record<string, unknown>;
  if (typeof it.uid !== 'string' || !UID_RE.test(it.uid)) return 'uid';
  if (typeof it.def !== 'string') return 'def';
  const def = equipDef(it.def);
  if (!def) return 'def';
  if (typeof it.grade !== 'string' || !GRADE_IDS.includes(it.grade as Grade)) return 'grade';
  const grade = it.grade as Grade;
  if ((grade === 'set') !== (def.grade === 'set')) return 'grade';
  const adds = Object.values(DIFFICULTY_ILVL_ADD) as number[];
  if (!nat(it.ilvl, 999) || !adds.some((a) => def.level + a === it.ilvl)) return 'ilvl';
  if (!Array.isArray(it.affixes)) return 'affixes';
  const gd = GRADES.find((g) => g.id === grade)!;
  const maxN = Math.min(gd.affixes[1], def.affixPool.length);
  const minN = Math.min(gd.affixes[0], def.affixPool.length);
  if (it.affixes.length < minN || it.affixes.length > maxN) return 'affix-count';
  const seen = new Set<string>();
  const affixes: { id: GearItem['affixes'][number]['id']; v: number }[] = [];
  for (const a of it.affixes as unknown[]) {
    if (!a || typeof a !== 'object') return 'affix';
    const { id, v } = a as { id?: unknown; v?: unknown };
    if (typeof id !== 'string' || seen.has(id) || !def.affixPool.includes(id as never)) return 'affix';
    const ad = affixDef(id as never);
    if (!ad || typeof v !== 'number' || !Number.isFinite(v)) return 'affix';
    const lo = Math.min(ad.range[0], ad.range[1]) - 1e-4, hi = Math.max(ad.range[0], ad.range[1]) + 1e-4;
    if (v < lo || v > hi) return 'affix-value';
    seen.add(id);
    affixes.push({ id: ad.id, v });
  }
  if (!nat(it.enh, ENHANCE_MAX)) return 'enh';
  if (!Array.isArray(it.pity) || it.pity.length !== ENHANCE_MAX + 1 || !it.pity.every((p) => nat(p, 9999))) return 'pity';
  const out: GearItem = { uid: it.uid, def: def.id, grade, ilvl: it.ilvl as number, affixes, enh: it.enh as number, pity: (it.pity as number[]).slice() };
  if (it.uniq !== undefined) {
    if (grade !== 'legend' || typeof it.uniq !== 'string') return 'uniq';
    const withU = { ...out, uniq: it.uniq };
    // 전설 고유 효과는 그 부위 후보여야 한다(PR #246 리뷰 P2)
    if (!uniqueOf(withU) || !uniquesFor(def.slot).some((x) => x.id === it.uniq)) return 'uniq';
    (out as { uniq?: string }).uniq = it.uniq;
  }
  // 레인 B 유니크 표시(GearItem.nu): 아는 이름 있는 유니크 정의여야 하고 부위가 맞아야 한다
  if (it.nu !== undefined) {
    if (typeof it.nu !== 'string') return 'nu';
    (out as unknown as { nu: string }).nu = it.nu;
    const u = namedOf(out);
    // rollNamedItem 규칙: 부위 일치 · 세트 아닌 일반 베이스 · 영웅 등급(PR #246 리뷰 P1 — 아무 장비에 nu를 붙여 거래 가능·시세를 바꾸지 못하게)
    if (!u || u.slot !== def.slot || def.setId || grade !== 'epic') return 'nu';
  }
  if (it.sock !== undefined) {
    const cap = itemSockets(out);
    if (!Array.isArray(it.sock) || it.sock.length > cap || !it.sock.every((c) => c === -1 || isGemCode(c))) return 'sock';
    out.sock = (it.sock as number[]).slice();
  }
  return out;
}

// ── 레인 B 어댑터 ──────────────────────────────────────

/** 거래 가능한 장비인가: 레인 B 정의(이름 있는 유니크 NAMED_UNIQUES[].tradable). 일반 장비 거래 허용은 2차 결정 사항(PRD 2장) */
export function isTradable(item: GearItem): boolean {
  return tradeInfo(item).tradable;
}
/** 시세 기준값: 레인 B marketBase(거래 가능 유니크만). 모르면 null */
export function tradeRef(item: GearItem): number | null {
  const t = tradeInfo(item);
  return t.tradable && t.marketBase > 0 ? Math.round(t.marketBase) : null;
}
/** 등록 가격 범위: 절대 범위 ∩ 기준값의 1/20 ~ 20배 */
export function priceBounds(item: GearItem): { min: number; max: number } {
  const ref = tradeRef(item);
  if (!ref) return { min: MARKET.minPrice, max: MARKET.maxPrice };
  return { min: Math.max(MARKET.minPrice, Math.floor(ref / 20)), max: Math.min(MARKET.maxPrice, ref * 20) };
}

// ── 돈 계산 ────────────────────────────────────────────

export function saleFee(price: number): number { return Math.floor(price * MARKET.feeRate); }
export function sellerGets(price: number): number { return price - saleFee(price); }
/** 다음 입찰 최소액: 입찰이 없으면 시작가, 있으면 +5%(최소 +1) */
export function minNextBid(start: number, bid: number): number {
  return bid > 0 ? Math.max(bid + 1, Math.ceil(bid * (1 + MARKET.minIncrement))) : start;
}
export function validPrice(v: unknown): v is number { return typeof v === 'number' && Number.isSafeInteger(v) && v >= MARKET.minPrice && v <= MARKET.maxPrice; }
